<script setup lang="ts">
import { ref, watch } from 'vue';
import { useVideoStore } from '../stores/videoStore';
import { usePlaylistStore } from '../stores/playlistStore';
import { fetchVideos, fetchPlaylists } from '../composables/useApi';
import UploadDialog from './UploadDialog.vue';

const videoStore = useVideoStore();
const playlistStore = usePlaylistStore();
const searchLocal = ref(videoStore.searchQuery);
const showUpload = ref(false);
const emit = defineEmits(['batchDelete']);

watch(searchLocal, (val) => {
  videoStore.setSearch(val);
});

async function onRefresh() {
  videoStore.loading = true;
  playlistStore.loading = true;
  try {
    const [videos, playlists] = await Promise.all([
      fetchVideos(true),
      fetchPlaylists(),
    ]);
    videoStore.allVideos = videos;
    videoStore.highlightDuplicates();
    videoStore.applyFilterAndSort();
    playlistStore.playlists = playlists;
  } catch (e) {
    console.error('Refresh failed:', e);
  } finally {
    videoStore.loading = false;
    playlistStore.loading = false;
  }
}

function onUploadComplete() {
  showUpload.value = false;
  onRefresh();
}

defineExpose({ refresh: onRefresh });
</script>

<template>
  <div class="toolbar">
    <div class="toolbar-inner">
      <div class="search-wrap">
        <svg class="search-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text"
          class="search-box"
          :value="searchLocal"
          @input="(e: Event) => searchLocal = (e.target as HTMLInputElement).value"
          placeholder="搜索视频名称..."
        />
        <button v-if="searchLocal" class="clear-btn" @click="searchLocal = ''">✕</button>
      </div>

      <div class="toolbar-actions">
        <select class="sort-select" :value="videoStore.sortBy" @change="videoStore.setSort(($event.target as HTMLSelectElement).value as any)">
          <option value="name_asc">名称 A-Z</option>
          <option value="name_desc">名称 Z-A</option>
          <option value="size_desc">大小 ↓</option>
          <option value="size_asc">大小 ↑</option>
        </select>

        <div class="view-toggle">
          <button
            class="view-btn"
            :class="{ active: videoStore.viewMode === 'grid' }"
            @click="videoStore.setView('grid')"
            title="网格视图"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          <button
            class="view-btn"
            :class="{ active: videoStore.viewMode === 'list' }"
            @click="videoStore.setView('list')"
            title="列表视图"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
              <rect x="3" y="4" width="4" height="4" rx="1"/>
              <rect x="3" y="10" width="4" height="4" rx="1"/>
              <rect x="3" y="16" width="4" height="4" rx="1"/>
            </svg>
          </button>
        </div>

        <button class="upload-btn" @click="showUpload = true" title="上传视频">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </button>

        <button class="refresh-btn" @click="onRefresh" title="刷新列表">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" class="refresh-icon">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
        </button>

        <button
          v-if="videoStore.selectedCount > 0"
          class="batch-delete-btn"
          @click="emit('batchDelete')"
          :title="`删除选中的 ${videoStore.selectedCount} 个视频`"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          <span class="batch-count">{{ videoStore.selectedCount }}</span>
        </button>
      </div>
    </div>
  </div>

  <UploadDialog
    v-if="showUpload"
    @close="showUpload = false"
    @complete="onUploadComplete"
  />
</template>

<style scoped>
.toolbar {
  position: sticky;
  top: 0;
  z-index: 100;
  margin: -24px -32px 24px;
  padding: 16px 32px;
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border-color);
  width: calc(100% + 64px);
}

.toolbar-inner {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-wrap {
  position: relative;
  flex: 1;
  max-width: 480px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.search-box {
  width: 100%;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 10px 36px 10px 38px;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: all var(--transition-normal);
}
.search-box:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-soft);
}
.search-box::placeholder { color: var(--text-muted); }

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: 4px;
  transition: color var(--transition-fast);
}
.clear-btn:hover { color: var(--text-primary); }

.toolbar-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
}

.sort-select {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 9px 12px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}
.sort-select:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-soft);
}

.view-toggle {
  display: flex;
  gap: 2px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 3px;
}

.view-btn {
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
}
.view-btn.active {
  background: var(--accent-soft);
  color: var(--accent);
}
.view-btn:hover:not(.active) { color: var(--text-secondary); }

.upload-btn,
.refresh-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 9px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
}
.upload-btn:hover {
  border-color: #22c55e;
  color: #22c55e;
}
.refresh-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.refresh-btn:hover .refresh-icon {
  animation: spin 0.8s linear infinite;
}

.batch-delete-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 9px 12px;
  color: #ef4444;
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
}
.batch-delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}
.batch-count {
  background: #ef4444;
  color: #fff;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 11px;
  min-width: 18px;
  text-align: center;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .toolbar {
    margin: 0;
    padding: 12px 16px;
    width: 100%;
  }

  .toolbar-inner {
    flex-wrap: wrap;
    gap: 8px;
    max-width: 100%;
  }

  .search-wrap {
    max-width: 100%;
    flex: 0 0 100%;
  }

  .search-box {
    padding: 12px 36px 12px 38px;
    font-size: 16px;
  }

  .toolbar-actions {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
  }

  .sort-select {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    font-size: 14px;
  }

  .view-toggle button,
  .refresh-btn {
    padding: 10px;
  }
}
</style>
